import type { FeatureFlags } from "@/features/config/featureFlags";

export const DATASETS_ROUTE = "/datasets";
/** The old Datasets (the curator), read-only, for accounts that have the new Datasets. */
export const LEGACY_DATASETS_ROUTE = "/datasets/legacy";

/**
 * Which Datasets page an account sees. The new Datasets (the dataset builder
 * service) replace the old curator. An account without them keeps the old
 * curator at /datasets until datasetBuilder is on for everyone.
 */
export type DatasetsAccess = {
  /** /datasets shows the new Datasets. */
  builder: boolean;
  /** Where the old curator lives: "primary" at /datasets, "legacy" read-only at /datasets/legacy. */
  curator: "primary" | "legacy" | null;
};

export const datasetsAccess = (
  flags: Pick<FeatureFlags, "dataCuration" | "datasetBuilder">,
  isDev: boolean,
): DatasetsAccess => {
  // Local dev gets the new Datasets unless only dataCuration is set, which is how
  // the end-to-end tests and the Feature flags page show an account without them.
  const builder = flags.datasetBuilder || (isDev && !flags.dataCuration);
  // The old curator's API answers only with dataCuration.
  const curator = isDev || flags.dataCuration ? (builder ? "legacy" : "primary") : null;
  return { builder, curator };
};

/** The page of an old (curator) dataset, or of its list without an ID. */
export const oldDatasetHref = (access: DatasetsAccess, datasetId?: string | null) => {
  const base = access.curator === "legacy" ? LEGACY_DATASETS_ROUTE : DATASETS_ROUTE;
  return datasetId ? `${base}?dataset=${encodeURIComponent(datasetId)}` : base;
};

/** Whether a /datasets URL points at the old curator: its dataset and new-chat parameters. */
export const isOldDatasetSearch = (search: string) => {
  const params = new URLSearchParams(search);
  return params.has("dataset") || params.has("chat");
};

/**
 * Where a request for a Datasets path goes, or null to render it here.
 * `path` is one of the Datasets routes; search and hash are kept on redirects.
 */
export const datasetsRedirect = (
  path: "/datasets" | "/datasets/fast" | "/datasets/legacy" | "/data-curation",
  access: DatasetsAccess,
  search = "",
  hash = "",
): string | null => {
  const keep = (to: string) => `${to}${search}${hash}`;
  if (!access.builder && !access.curator) return "/";
  switch (path) {
    case "/datasets":
      if (!access.builder) return null;
      return access.curator === "legacy" && isOldDatasetSearch(search)
        ? keep(LEGACY_DATASETS_ROUTE)
        : null;
    case "/datasets/fast":
      return keep(DATASETS_ROUTE);
    case "/datasets/legacy":
      return access.curator === "legacy" ? null : keep(DATASETS_ROUTE);
    case "/data-curation":
      return keep(access.curator === "legacy" ? LEGACY_DATASETS_ROUTE : DATASETS_ROUTE);
  }
};
